import React, { useEffect, useRef, useMemo } from "react";
import ReactECharts from "echarts-for-react";

const TwoComparisionBarChart = ({
  data = [],
  heading,
  legendData = [],
  ChartHeight = 300,
}) => {
  const chartRef = useRef();

  const xAxisData = useMemo(() => data.map((item) => item?.key), [data]);
  const lmtdData = useMemo(() => data.map((item) => item?.lmtd?.value), [data]);
  const mtdData = useMemo(() => data.map((item) => item?.mtd?.value), [data]);

  const visibleBars = 8;
  const dataZoomEnabled = data.length > visibleBars;
  const dataZoomEnd = dataZoomEnabled ? (visibleBars / data.length) * 100 : 100;

  useEffect(() => {
    setTimeout(() => {
      chartRef.current?.getEchartsInstance()?.resize();
    }, 200);
  }, [data]);

  const option = {
    title: {
      text: heading,
      left: "left",
      textStyle: {
        fontFamily: "Uni Neue",
        fontSize: 18,
        fontWeight: "700",
        color: "#1F2937",
      },
    },

    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: (params) =>
        `${params[0].axisValue}<br/>` +
        params
          .map(
            (item) =>
              `${item.seriesName}: ${item.value}${
                heading === "Samridhi Score" ? "%" : ""
              }`
          )
          .join("<br/>"),
    },

    legend: {
      data: legendData,
      top: "5%",
      right: "2%",
    },

    grid: {
      left: "0%",
      right: "0%",
      top: "15%",
      bottom: dataZoomEnabled ? "12%" : "4%",
      containLabel: true,
    },

    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        start: 0,
        end: dataZoomEnd,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
      },
      {
        type: "slider",
        show: dataZoomEnabled,
        xAxisIndex: 0,
        start: 0,
        end: dataZoomEnd,
        height: 12,
        bottom: 0,
        showDetail: false,

        zoomLock: true,
        zoomOnMouseWheel: false,
        moveOnMouseMove: false,
        moveOnMouseWheel: false,

        // 🎨 Rounded styling
        fillerColor: "#2A5775",
        backgroundColor: "#E5E7EB",
        borderRadius: 8,
        handleSize: 12,
        handleStyle: {
          color: "#2A5775",
          borderRadius: 6,
        },
        moveHandleSize: 0,
        showDataShadow: false,
      },
    ],

    xAxis: {
      type: "category",
      data: xAxisData,
      axisLabel: {
        interval: 0,
        formatter: (value) => {
          if (heading === "Samridhi Score" || heading === "CPSC") return value;

          const words = value.match(/[^&/\s]+[&/]*|[&/]+/g) || [];
          const lines = [];
          let currentLine = words.shift() || "";

          words.forEach((word) => {
            if ((currentLine + " " + word).length > 10) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine += " " + word;
            }
          });

          lines.push(currentLine);
          return lines.join("\n");
        },
        color: "#000",
        fontWeight: "400",
      },
    },

    yAxis: {
      type: "value",
      axisLabel: { show: false },
      splitLine: { show: false },
    },

    series: [
      {
        name: legendData?.[0] || "MTD",
        type: "bar",
        data: mtdData,
        barWidth: 15,
        barGap: "40%",
        barMinHeight: 30,
        itemStyle: {
          color: "#5470c6",
          borderRadius: [5, 5, 0, 0],
        },
        label: {
          show: true,
          position: "inside",
          rotate: 90,
          color: "#fff",
          fontSize: 12,
          formatter: (p) =>
            `${p.value}${heading === "Samridhi Score" ? "%" : ""}`,
        },
      },
      {
        name: legendData?.[1] || "LMTD",
        type: "bar",
        data: lmtdData,
        barWidth: 15,
        barGap: "40%",
        barMinHeight: 30,
        itemStyle: {
          color: "#e0aaba",
          borderRadius: [5, 5, 0, 0],
        },
        label: {
          show: true,
          position: "inside",
          rotate: 90,
          color: "#fff",
          fontSize: 12,
          formatter: (p) =>
            `${p.value}${heading === "Samridhi Score" ? "%" : ""}`,
        },
      },
    ],
  };

  return (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ width: "100%", height: ChartHeight }}
      notMerge
      lazyUpdate
    />
  );
};

export default TwoComparisionBarChart;
